import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  RingProgress,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuArrowDownToLine,
  LuArrowUpFromLine,
  LuPlus,
  LuTrendingDown,
  LuTrendingUp,
  LuTriangleAlert,
} from 'react-icons/lu'
import { Chart } from '@/frontend/components/marina/Chart'
import { formatIdr } from '@/frontend/components/marina/CurrencyText'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { EmptyState } from '@/frontend/components/marina/EmptyState'
import { InsightStrip } from '@/frontend/components/marina/InsightStrip'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { LiveChatBubble } from '@/frontend/components/marina/LiveChatBubble'
import { OmbakPattern } from '@/frontend/components/marina/OmbakPattern'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { SunriseSunsetWidget } from '@/frontend/components/marina/SunriseSunsetWidget'
import { WeatherCard } from '@/frontend/components/marina/WeatherCard'
import { useAnalyticsBoats, useAnalyticsOverview, useAnalyticsRevenue } from '@/frontend/hooks/useAnalytics'
import { useCanWrite, useSession } from '@/frontend/hooks/useAuth'
import { useArrivalsDepartures } from '@/frontend/hooks/useOccupancy'
import styles from './index.module.css'

export const Route = createFileRoute('/dashboard/')({
  component: DashboardOverview,
})

function getGreeting(): string {
  const h = dayjs().hour()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function DashboardOverview() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const canWrite = useCanWrite()
  const firstName = session?.user?.name?.split(' ')[0] ?? 'there'

  const { data, isLoading } = useAnalyticsOverview()
  const { data: revenueData } = useAnalyticsRevenue(6)
  const { data: boatsData } = useAnalyticsBoats()

  const freeBerths = data?.freeBerths ?? 0
  const occupiedBerths = data?.occupiedBerths ?? 0
  const totalBerths = freeBerths + occupiedBerths
  const occupancyRate = totalBerths > 0 ? Math.round((occupiedBerths / totalBerths) * 100) : 0
  const _pendingTotal = (data?.pendingCount ?? 0) + (data?.waitlistedCount ?? 0)

  const buckets = revenueData?.buckets ?? []
  const _sparkValues = buckets.map((b) => b.revenue)
  const currentMonthRevenue = buckets[buckets.length - 1]?.revenue ?? 0
  const prevMonthRevenue = buckets[buckets.length - 2]?.revenue ?? 0
  const _momDelta =
    prevMonthRevenue > 0 ? Math.round(((currentMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100) : null

  const arrivedToday = boatsData?.boatsArrivedToday ?? 0
  const departedToday = boatsData?.boatsDepartedToday ?? 0
  const _netToday = arrivedToday - departedToday
  const _delayedCount = boatsData?.delayedArrivals?.length ?? 0
  const _overdueCount = revenueData?.statusMix?.find((s) => s.status === 'OVERDUE')?.count ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* ── Hero greeting (glass card with ombak watermark) ── */}
        <div className={styles.heroCard}>
          <div className={styles.heroContent}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
              <Stack gap={6}>
                <Text className={styles.displayHeading} style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}>
                  {getGreeting()}, {firstName}
                </Text>
                <Text size="sm" c="dimmed" style={{ fontStyle: 'italic', fontFamily: 'var(--font-display)' }}>
                  {dayjs().format('dddd, D MMMM YYYY')} · Pelabuhan Anda hari ini
                </Text>
              </Stack>
              {canWrite && (
                <Button
                  color="karang"
                  leftSection={<LuPlus size={16} />}
                  onClick={() => navigate({ to: '/dashboard/bookings/new' as any })}
                  style={{ flexShrink: 0 }}
                >
                  {t('page.overview.create_booking')}
                </Button>
              )}
            </Group>
            <Box mt="md">
              <SunriseSunsetWidget />
            </Box>
          </div>
          <div className={styles.heroOmbak}>
            <OmbakPattern height={24} opacity={0.32} stroke={1} />
          </div>
        </div>

        {/* ── Insight strip (actionable cards) ── */}
        <InsightStrip />

        {/* ── Main grid ── */}
        <Grid gutter="md">
          {/* LEFT ~65% */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              <PendingRequestsCard requests={data?.pendingRequests ?? []} isLoading={isLoading} />
              <RevenueTrendCard buckets={buckets} isLoading={!revenueData} />
              <RecentBookingsCard bookings={data?.recentBookings ?? []} isLoading={isLoading} />
            </Stack>
          </Grid.Col>

          {/* RIGHT ~35% */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              <OccupancyRingCard
                free={freeBerths}
                occupied={occupiedBerths}
                rate={occupancyRate}
                isLoading={isLoading}
              />
              <TodayScheduleCard />
              <Box visibleFrom="md">
                <WeatherCard height={240} />
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <LiveChatBubble />
    </Container>
  )
}

// ─── Alert Strip ────────────────────────────────────

function _AlertStrip({ delayedCount, overdueCount }: { delayedCount: number; overdueCount: number }) {
  if (delayedCount === 0 && overdueCount === 0) return null

  const parts: string[] = []
  if (delayedCount > 0) parts.push(`${delayedCount} delayed arrival${delayedCount > 1 ? 's' : ''}`)
  if (overdueCount > 0) parts.push(`${overdueCount} overdue invoice${overdueCount > 1 ? 's' : ''}`)

  const primaryLink = delayedCount > 0 ? '/dashboard/analytics' : '/dashboard/billing/invoices'

  return (
    <Alert variant="light" color="orange" icon={<LuTriangleAlert size={18} />} radius="md" title="Needs your attention">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text size="sm">{parts.join(' · ')}</Text>
        <Anchor component={Link} to={primaryLink as any} c="orange" size="sm" fw={500}>
          Review →
        </Anchor>
      </Group>
    </Alert>
  )
}

// ─── Stat Card ──────────────────────────────────────

function _StatCard({
  icon,
  value,
  label,
  color,
  sub,
  small,
  onClick,
  trend,
  sparkline,
}: {
  icon: ReactNode
  value: ReactNode
  label: string
  color: string
  sub?: string
  small?: boolean
  onClick?: () => void
  trend?: number | null
  sparkline?: object
}) {
  const inner = (
    <Card
      radius="md"
      padding="md"
      className={styles.liftOnHover}
      style={onClick ? { cursor: 'pointer', overflow: 'hidden' } : { overflow: 'hidden' }}
    >
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <ThemeIcon variant="light" color={color} size="lg" radius="lg" style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text size={small ? 'md' : 'xl'} fw={700} lh={1.2} truncate>
            {value}
          </Text>
          <Text size="xs" c="dimmed" mt={2} lh={1.3}>
            {label}
          </Text>
          {typeof trend === 'number' && trend !== 0 && <TrendIndicator delta={trend} />}
          {sub && (
            <Text size="xs" c={color} mt={2} fw={500}>
              {sub}
            </Text>
          )}
        </div>
      </Group>
      {sparkline && (
        <Box mt="xs" mx={-12} mb={-12} style={{ pointerEvents: 'none' }}>
          <Chart option={sparkline as any} height={36} />
        </Box>
      )}
    </Card>
  )
  if (onClick) {
    return (
      <UnstyledButton onClick={onClick} style={{ display: 'block', width: '100%', textAlign: 'left' }}>
        {inner}
      </UnstyledButton>
    )
  }
  return inner
}

function TrendIndicator({ delta }: { delta: number }) {
  const isUp = delta > 0
  return (
    <Group gap={3} mt={3} wrap="nowrap">
      {isUp ? (
        <LuTrendingUp size={12} color="var(--mantine-color-teal-6)" />
      ) : (
        <LuTrendingDown size={12} color="var(--mantine-color-red-6)" />
      )}
      <Text size="xs" c={isUp ? 'teal.7' : 'red.7'} fw={500}>
        {Math.abs(delta)}% vs last month
      </Text>
    </Group>
  )
}

// ─── Pending Requests ────────────────────────────────

function PendingRequestsCard({
  requests,
  isLoading,
}: {
  requests: NonNullable<ReturnType<typeof useAnalyticsOverview>['data']>['pendingRequests']
  isLoading: boolean
}) {
  const navigate = useNavigate()

  return (
    <Card radius="md" padding="lg">
      <Group justify="space-between" mb="md">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
          Pending &amp; Waitlisted Requests
        </Text>
        <Anchor component={Link} to="/dashboard/reservations/requests" c="laut" size="xs">
          View all →
        </Anchor>
      </Group>

      {isLoading && (
        <Stack gap="xs">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={52} radius="md" />
          ))}
        </Stack>
      )}

      {!isLoading && requests.length === 0 && (
        <EmptyState
          kind="anchor"
          size="sm"
          title="Semua jelas — tidak ada permintaan tertunda"
          description="Saat ada booking baru menanti dermaga, mereka akan muncul di sini."
        />
      )}

      {!isLoading && requests.length > 0 && (
        <Stack gap={2}>
          {requests.map((r) => {
            const name = `${r.customer.firstName} ${r.customer.lastName}`
            const daysUntil = dayjs(r.checkInAt).diff(dayjs(), 'day')
            const isOverdue = daysUntil < 0
            const isUrgent = !isOverdue && daysUntil < 3
            const accentColor = isOverdue ? 'red.5' : isUrgent ? 'orange.5' : 'blue.4'
            const countdownColor: string = isOverdue ? 'red' : isUrgent ? 'orange' : 'dimmed'
            const countdownText = isOverdue ? `${-daysUntil}d overdue` : daysUntil === 0 ? 'Today' : `in ${daysUntil}d`

            return (
              <UnstyledButton
                key={r.id}
                onClick={() => navigate({ to: '/dashboard/reservations/requests' as any })}
                style={{ display: 'block', borderRadius: 8 }}
              >
                <Group wrap="nowrap" gap="sm" px="xs" py={6} style={{ borderRadius: 8 }}>
                  <Box w={3} h={44} bg={accentColor} style={{ borderRadius: 2, flexShrink: 0 }} />
                  <CustomerAvatar name={name} size={36} />
                  <Stack gap={1} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={600} truncate>
                      {name}
                    </Text>
                    <Group gap={4} wrap="nowrap">
                      <Text size="xs" c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>
                        {r.boat.name}
                        {r.boat.type ? ` · ${r.boat.type.replace(/_/g, ' ')}` : ''}
                      </Text>
                      <KindBadge category={r.customer.category} />
                    </Group>
                  </Stack>
                  <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
                    <StatusBadge status={r.status} kind="booking" upper />
                    <Text size="xs" c={countdownColor} fw={500}>
                      {countdownText}
                    </Text>
                  </Stack>
                </Group>
              </UnstyledButton>
            )
          })}
        </Stack>
      )}
    </Card>
  )
}

// ─── Revenue Trend ───────────────────────────────────

function RevenueTrendCard({
  buckets,
  isLoading,
}: {
  buckets: { month: string; revenue: number }[]
  isLoading: boolean
}) {
  const total = buckets.reduce((s, b) => s + b.revenue, 0)
  const avg = buckets.length > 0 ? total / buckets.length : 0
  const hasData = buckets.some((b) => b.revenue > 0)

  return (
    <Card radius="md" padding="lg">
      <Group justify="space-between" mb="md">
        <div>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
            Revenue Trend
          </Text>
          {!isLoading && hasData && (
            <Text size="xs" c="dimmed" mt={2}>
              Last 6 months · Avg {formatIdr(avg)}
            </Text>
          )}
        </div>
        <Anchor component={Link} to="/dashboard/analytics" c="laut" size="xs">
          View analytics →
        </Anchor>
      </Group>

      {isLoading && <Skeleton height={180} radius="md" />}

      {!isLoading && !hasData && (
        <Box py="xl" ta="center">
          <Text size="sm" c="dimmed">
            No revenue data yet
          </Text>
        </Box>
      )}

      {!isLoading && hasData && <Chart option={buildRevenueChart(buckets)} height={200} />}
    </Card>
  )
}

// ─── Recent Bookings ─────────────────────────────────

function RecentBookingsCard({
  bookings,
  isLoading,
}: {
  bookings: NonNullable<ReturnType<typeof useAnalyticsOverview>['data']>['recentBookings']
  isLoading: boolean
}) {
  return (
    <Card radius="md" padding="lg">
      <Group justify="space-between" mb="md">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
          Recent Bookings
        </Text>
        <Anchor component={Link} to="/dashboard/reservations/bookings" c="laut" size="xs">
          View all →
        </Anchor>
      </Group>

      {isLoading && (
        <Stack gap="xs">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={44} radius="md" />
          ))}
        </Stack>
      )}

      {!isLoading && bookings.length === 0 && (
        <EmptyState
          kind="dock"
          size="sm"
          title="Dermaga sunyi"
          description="Booking terbaru akan muncul di sini ketika kapal mulai berdatangan."
        />
      )}

      {!isLoading && bookings.length > 0 && (
        <Stack gap={2}>
          {bookings.map((b) => {
            const name = `${b.customer.firstName} ${b.customer.lastName}`
            return (
              <Link
                key={b.id}
                to="/dashboard/reservations/bookings"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Group justify="space-between" px="xs" py={7} style={{ borderRadius: 8 }} wrap="nowrap">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <CustomerAvatar name={name} size={32} />
                    <Stack gap={0} style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>
                        {name}
                      </Text>
                      <Text size="xs" c="dimmed" truncate>
                        {b.boat.name}
                        {b.berth ? ` · ${b.berth.code}` : ''}
                        {' · '}
                        {dayjs(b.checkInAt).format('D MMM')} → {dayjs(b.checkOutAt).format('D MMM')}
                      </Text>
                    </Stack>
                  </Group>
                  <StatusBadge status={b.status} kind="booking" upper />
                </Group>
              </Link>
            )
          })}
        </Stack>
      )}
    </Card>
  )
}

// ─── Occupancy Ring ──────────────────────────────────

function OccupancyRingCard({
  free,
  occupied,
  rate,
  isLoading,
}: {
  free: number
  occupied: number
  rate: number
  isLoading: boolean
}) {
  const total = free + occupied
  const ringColor = rate > 80 ? 'red.5' : rate > 60 ? 'orange.5' : 'teal.5'

  return (
    <Card radius="md" padding="md">
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="md" style={{ letterSpacing: '0.06em' }}>
        Occupancy
      </Text>
      {isLoading ? (
        <Skeleton height={80} radius="md" />
      ) : (
        <Group gap="lg" justify="center">
          <RingProgress
            size={90}
            thickness={9}
            roundCaps
            sections={[{ value: rate, color: ringColor }]}
            label={
              <Text ta="center" fw={700} size="sm" lh={1}>
                {rate}%
              </Text>
            }
          />
          <Stack gap={4}>
            <Group gap={6}>
              <Box w={8} h={8} bg="laut.5" style={{ borderRadius: 2 }} />
              <Text size="xs" fw={500}>
                {occupied} occupied
              </Text>
            </Group>
            <Group gap={6}>
              <Box w={8} h={8} bg="gray.3" style={{ borderRadius: 2 }} />
              <Text size="xs" c="dimmed">
                {free} free
              </Text>
            </Group>
            <Text size="xs" c="dimmed" mt={2}>
              {total} berths total
            </Text>
          </Stack>
        </Group>
      )}
    </Card>
  )
}

// ─── Today's Schedule ────────────────────────────────

function TodayScheduleCard() {
  const today = dayjs().format('YYYY-MM-DD')
  const { data, isLoading } = useArrivalsDepartures({ date: today, type: 'all' })

  const allEvents = [...(data?.arrivals ?? []), ...(data?.departures ?? [])]
    .sort((a, b) => dayjs(a.scheduledAt).diff(dayjs(b.scheduledAt)))
    .slice(0, 5)

  return (
    <Card radius="md" padding="md">
      <Group justify="space-between" mb="md">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.06em' }}>
          Today's Schedule
        </Text>
        <Text size="xs" c="dimmed">
          {dayjs().format('ddd, D MMM')}
        </Text>
      </Group>

      {isLoading && <Skeleton height={80} radius="md" />}

      {!isLoading && allEvents.length === 0 && (
        <Text size="sm" c="dimmed" py="xs">
          Nothing scheduled today
        </Text>
      )}

      {!isLoading && allEvents.length > 0 && (
        <Stack gap="xs">
          {allEvents.map((row) => {
            const isArrival = row.kind === 'arrival'
            return (
              <Group key={`${row.id}-${row.kind}`} gap="sm" wrap="nowrap">
                <ThemeIcon
                  variant="light"
                  color={isArrival ? 'teal' : 'orange'}
                  size="sm"
                  radius="xl"
                  style={{ flexShrink: 0 }}
                >
                  {isArrival ? <LuArrowDownToLine size={12} /> : <LuArrowUpFromLine size={12} />}
                </ThemeIcon>
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={600} truncate>
                    {row.boat.name}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {row.berth?.code ?? '—'} · {dayjs(row.scheduledAt).format('HH:mm')}
                  </Text>
                </Stack>
                <Badge variant="light" color={isArrival ? 'teal' : 'orange'} size="xs">
                  {isArrival ? 'Arrival' : 'Departure'}
                </Badge>
              </Group>
            )
          })}
        </Stack>
      )}
    </Card>
  )
}

// ─── ECharts option builders ─────────────────────────

function _buildSparklineOption(values: number[], colorRgb: string): any {
  return {
    grid: { left: 0, right: 0, top: 4, bottom: 4 },
    xAxis: {
      show: false,
      type: 'category',
      boundaryGap: false,
      data: values.map((_, i) => String(i)),
    },
    yAxis: { show: false, type: 'value', scale: true },
    series: [
      {
        type: 'line',
        data: values,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: `rgb(${colorRgb})` },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: `rgba(${colorRgb}, 0.3)` },
              { offset: 1, color: `rgba(${colorRgb}, 0)` },
            ],
          },
        },
      },
    ],
    tooltip: { show: false },
  }
}

function buildRevenueChart(buckets: { month: string; revenue: number }[]): any {
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: '#0E7C7B', opacity: 0.4 } },
      formatter: (params: any) => {
        const p = params[0]
        return `<b>${p.name}</b><br/>${formatIdr(p.value)}`
      },
    },
    grid: { left: 56, right: 12, top: 16, bottom: 32 },
    xAxis: {
      type: 'category',
      data: buckets.map((b) => b.month),
      axisLabel: { fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 11,
        formatter: (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(0)}M` : `${(v / 1_000).toFixed(0)}K`),
      },
      splitLine: { lineStyle: { type: 'dashed', opacity: 0.4 } },
    },
    series: [
      {
        type: 'bar',
        data: buckets.map((b) => b.revenue),
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#1A9C98' },
              { offset: 1, color: '#0E7C7B' },
            ],
          },
          borderRadius: [8, 8, 0, 0],
          shadowColor: 'rgba(14, 124, 123, 0.25)',
          shadowBlur: 8,
          shadowOffsetY: 2,
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#3AAFAB' },
                { offset: 1, color: '#1A9C98' },
              ],
            },
          },
        },
        barMaxWidth: 36,
      },
    ],
    animationDuration: 800,
    animationEasing: 'cubicOut',
  }
}
